import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  memo,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Heart,
  Download,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  Send,
} from "lucide-react";
import OptimizedImage from "./OptimizedImage";
import RegisterPrompt from "./RegisterPrompt";
import PhotographerInvite from "./PhotographerInvite";

// ===== HELPER PARA FORMATEAR FECHAS EN ZONA HORARIA DE MÉXICO =====
const formatDateMX = (dateString, options = {}) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    // Si la fecha no tiene zona horaria, asumimos que viene de MySQL en horario de México
    // y la convertimos correctamente
    const defaultOptions = {
      timeZone: "America/Mexico_City",
      day: "numeric",
      month: "short",
      ...options,
    };
    return date.toLocaleDateString("es-MX", defaultOptions);
  } catch (e) {
    console.error("Error formateando fecha:", e);
    return "";
  }
};

const formatDateTimeMX = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-MX", {
      timeZone: "America/Mexico_City",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    console.error("Error formateando fecha:", e);
    return "";
  }
};

// ===== CACHE DE COMENTARIOS ESTILO INSTAGRAM =====
// Evita requests repetidos al navegar entre fotos
const commentsCache = new Map();
const CACHE_TTL = 60000; // 1 minuto

const getCachedComments = (photoId) => {
  const cached = commentsCache.get(photoId);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  return null;
};

const setCachedComments = (photoId, comments) => {
  commentsCache.set(photoId, {
    data: comments,
    timestamp: Date.now(),
  });
};

// Limpiar cache vieja periódicamente
setInterval(() => {
  const now = Date.now();
  commentsCache.forEach((value, key) => {
    if (now - value.timestamp > CACHE_TTL * 2) {
      commentsCache.delete(key);
    }
  });
}, CACHE_TTL);

const PhotoModal = memo(
  ({
    photo,
    isOpen,
    onClose,
    onLike,
    onNext,
    onPrev,
    hasNext,
    hasPrev,
    onRequestAuth = () => {},
  }) => {
    const [startX, setStartX] = useState(null);
    const [currentX, setCurrentX] = useState(null);
    const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);
    const [showPhotographerInvite, setShowPhotographerInvite] = useState(false);
    const [commentText, setCommentText] = useState("");
    const [comments, setComments] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showComments, setShowComments] = useState(false);
    const [lastTap, setLastTap] = useState(0);
    const [showLikeAnimation, setShowLikeAnimation] = useState(false);
    const [localLikes, setLocalLikes] = useState(photo?.likes || 0);
    const [isInputFocused, setIsInputFocused] = useState(false);

    // Ref para el contenedor del modal
    const modalContainerRef = useRef(null);
    const commentsPanelRef = useRef(null);
    const commentsInputRef = useRef(null);
    const commentsListRef = useRef(null);

    // Guardar la altura inicial del viewport (antes de que aparezca el teclado)
    const initialViewportHeight = useRef(
      typeof window !== "undefined" ? window.innerHeight : 0
    );

    // ===== SOLUCIÓN ESTILO INSTAGRAM PARA iOS KEYBOARD =====
    // El truco es NO usar visualViewport para la altura, sino mantener
    // la altura fija y dejar que iOS maneje el scroll internamente
    useEffect(() => {
      if (!showComments) return;

      // Guardar altura inicial cuando se abre el panel
      initialViewportHeight.current = window.innerHeight;

      // Función para manejar cuando el input recibe focus
      const handleFocus = () => {
        setIsInputFocused(true);
        // Scroll la lista de comentarios hacia abajo para que el input sea visible
        setTimeout(() => {
          if (commentsListRef.current) {
            commentsListRef.current.scrollTop =
              commentsListRef.current.scrollHeight;
          }
        }, 100);
      };

      // Función para manejar cuando el input pierde focus
      const handleBlur = () => {
        setIsInputFocused(false);
        // Forzar un reflow para asegurar que el layout se restaure
        setTimeout(() => {
          if (commentsPanelRef.current) {
            commentsPanelRef.current.style.height = "100%";
            // Forzar repaint
            void commentsPanelRef.current.offsetHeight;
          }
          // Scroll window back to top
          window.scrollTo(0, 0);
        }, 100);
      };

      const inputElement = commentsInputRef.current;
      if (inputElement) {
        inputElement.addEventListener("focus", handleFocus);
        inputElement.addEventListener("blur", handleBlur);
      }

      return () => {
        if (inputElement) {
          inputElement.removeEventListener("focus", handleFocus);
          inputElement.removeEventListener("blur", handleBlur);
        }
      };
    }, [showComments]);

    useEffect(() => {
      if (isOpen) {
        document.body.style.overflow = "hidden";
        // Hacer scroll al inicio al abrir el modal
        setTimeout(() => {
          if (modalContainerRef.current) {
            modalContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
          }
          window.scrollTo({ top: 0, behavior: "smooth" });
        }, 100);
      } else {
        document.body.style.overflow = "unset";
      }
      return () => {
        document.body.style.overflow = "unset";
      };
    }, [isOpen, photo?.id]);

    // Efecto para hacer scroll al inicio y cerrar comentarios cuando cambia la foto
    useEffect(() => {
      if (photo?.id) {
        // Cerrar panel de comentarios al cambiar de foto
        setShowComments(false);
        // Resetear estados de comentarios
        setCommentText("");
        setIsSubmitting(false);
        // Hacer scroll al inicio
        setTimeout(() => {
          if (modalContainerRef.current) {
            modalContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
          }
          window.scrollTo({ top: 0, behavior: "smooth" });
        }, 100);
      }
    }, [photo?.id]);

    // Sincronizar localLikes cuando cambia la foto
    useEffect(() => {
      setLocalLikes(photo?.likes || 0);
    }, [photo?.likes]);

    // ===== FUNCIÓN DE FETCH OPTIMIZADA CON CACHE =====
    const fetchCommentsForPhoto = useCallback(
      async (photoId, forceRefresh = false) => {
        if (!photoId) return;

        // 1. Verificar cache primero (respuesta instantánea)
        if (!forceRefresh) {
          const cached = getCachedComments(photoId);
          if (cached) {
            setComments(cached);
            return;
          }
        }

        try {
          // 2. Fetch con paginación (solo primeros 10 comentarios)
          const response = await fetch(
            `/api/comments.php?photo_id=${photoId}&limit=10`
          );
          if (response.ok) {
            const data = await response.json();
            // Soportar nuevo formato con paginación o formato legacy (array directo)
            const commentsList = Array.isArray(data)
              ? data
              : data.comments || [];
            setComments(commentsList);
            // Guardar en cache
            setCachedComments(photoId, commentsList);
          }
        } catch (error) {
          console.error("Error al cargar comentarios:", error);
          setComments([]);
        }
      },
      []
    );

    // ===== PRE-FETCH DE FOTOS ADYACENTES (Estilo Instagram) =====
    const prefetchAdjacentComments = useCallback(
      (currentPhotoId, allPhotos) => {
        if (!allPhotos || !currentPhotoId) return;

        const currentIndex = allPhotos.findIndex(
          (p) => p.id === currentPhotoId
        );
        if (currentIndex === -1) return;

        // Pre-cargar comentarios de foto anterior y siguiente
        const adjacentIndices = [currentIndex - 1, currentIndex + 1];
        adjacentIndices.forEach((index) => {
          if (index >= 0 && index < allPhotos.length) {
            const adjacentPhotoId = allPhotos[index].id;
            // Solo pre-cargar si no está en cache
            if (!getCachedComments(adjacentPhotoId)) {
              // Usar requestIdleCallback para no bloquear UI
              if ("requestIdleCallback" in window) {
                requestIdleCallback(() => {
                  fetch(
                    `/api/comments.php?photo_id=${adjacentPhotoId}&limit=10`
                  )
                    .then((r) => r.json())
                    .then((data) => {
                      // Soportar nuevo formato con paginación o formato legacy
                      const comments = Array.isArray(data)
                        ? data
                        : data.comments || [];
                      setCachedComments(adjacentPhotoId, comments);
                    })
                    .catch(() => {}); // Silenciar errores de pre-fetch
                });
              }
            }
          }
        });
      },
      []
    );

    // Cargar comentarios cuando cambia la foto (con cache)
    useEffect(() => {
      if (photo?.id && isOpen) {
        fetchCommentsForPhoto(photo.id);
      } else {
        // Resetear comentarios si no hay foto
        setComments([]);
      }
    }, [photo?.id, isOpen, fetchCommentsForPhoto]);

    // ===== HANDLERS MEMOIZADOS =====
    const handleTouchStart = useCallback((e) => {
      // Compatibilidad con todos los eventos táctiles
      const touch = e.touches ? e.touches[0] : e;
      setStartX(touch.clientX);

      // Haptic feedback en dispositivos compatibles (iOS, Android)
      try {
        if (typeof navigator !== "undefined" && navigator.vibrate) {
          navigator.vibrate(10);
        }
      } catch (err) {
        // Silenciar errores en navegadores sin soporte
      }
    }, []);

    const handleTouchMove = useCallback(
      (e) => {
        // Compatibilidad con todos los eventos táctiles
        const touch = e.touches ? e.touches[0] : e;
        setCurrentX(touch.clientX);

        // Prevenir scroll vertical mientras se hace swipe horizontal
        if (Math.abs(startX - touch.clientX) > 10) {
          if (e.preventDefault) {
            e.preventDefault();
          }
          if (e.stopPropagation) {
            e.stopPropagation();
          }
        }
      },
      [startX]
    );

    const handleTouchEnd = useCallback(() => {
      const diff = startX - currentX;
      const threshold =
        (typeof window !== "undefined" ? window.innerWidth : 320) * 0.2;

      if (Math.abs(diff) > threshold) {
        // Haptic feedback al cambiar foto
        try {
          if (typeof navigator !== "undefined" && navigator.vibrate) {
            navigator.vibrate(20);
          }
        } catch (err) {
          // Silenciar errores
        }

        if (diff > 0 && hasNext) {
          onNext();
        } else if (diff < 0 && hasPrev) {
          onPrev();
        }
      }
      setStartX(0);
      setCurrentX(0);
    }, [startX, currentX, hasNext, hasPrev, onNext, onPrev]);

    const handleDownload = useCallback(() => {
      const link = document.createElement("a");
      link.href = photo.url;
      link.download = `foto-${photo.id}.jpg`;
      link.click();
    }, [photo?.url, photo?.id]);

    const handleDoubleTap = useCallback(
      (e) => {
        const now = Date.now();
        const DOUBLE_TAP_DELAY = 300;

        if (now - lastTap < DOUBLE_TAP_DELAY && now - lastTap > 0) {
          e.preventDefault();
          // Trigger like
          handleLikeClick();
          // Mostrar animación de corazón
          setShowLikeAnimation(true);
          if (window.navigator && window.navigator.vibrate) {
            window.navigator.vibrate(50);
          }
          setTimeout(() => setShowLikeAnimation(false), 1000);
        }
        setLastTap(now);
      },
      [lastTap]
    );

    const handleCommentClick = useCallback(() => {
      const uploaderName = localStorage.getItem("uploaderName");
      if (!uploaderName) {
        setShowRegisterPrompt(true);
      } else {
        // En móvil, abrir el panel de comentarios
        setShowComments(true);
        // En desktop, enfocarse en el input
        setTimeout(() => {
          const commentInput = document.querySelector(
            'input[placeholder="Agregar un comentario..."]'
          );
          if (commentInput && window.innerWidth >= 1024) {
            commentInput.focus();
          }
        }, 100);
      }
    }, []);

    const handleCommentSubmit = useCallback(
      async (e) => {
        e.preventDefault();
        if (!commentText.trim() || isSubmitting) return;

        const uploaderName = localStorage.getItem("uploaderName");
        if (!uploaderName) {
          setShowRegisterPrompt(true);
          return;
        }

        const commentToSend = commentText.trim();
        const currentPhotoId = photo?.id;

        // === OPTIMISTIC UPDATE: Mostrar inmediatamente ===
        const tempId = `temp_${Date.now()}`;
        const optimisticComment = {
          id: tempId,
          author: uploaderName,
          text: commentToSend,
          timestamp: new Date().toISOString(),
        };

        // Agregar comentario inmediatamente a la UI
        setComments((prevComments) => [...prevComments, optimisticComment]);
        // Limpiar el input inmediatamente
        setCommentText("");
        // NO cerrar el panel de comentarios - mantenerlo abierto para que el usuario pueda seguir comentando

        // Enviar al servidor en segundo plano (sin bloquear UI)
        try {
          const response = await fetch("/api/comments.php", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              photo_id: photo.id,
              user_name: uploaderName,
              comment: commentToSend,
            }),
          });

          if (response.ok) {
            const result = await response.json();
            // Actualizar el comentario temporal con el ID real del servidor
            if (result.comment?.id) {
              setComments((prevComments) => {
                const updatedComments = prevComments.map((c) =>
                  c.id === tempId ? { ...result.comment } : c
                );
                // Actualizar cache con los comentarios actualizados
                setCachedComments(currentPhotoId, updatedComments);
                return updatedComments;
              });
            }
          } else {
            // Si falla, remover el comentario optimista
            console.error("Error en la respuesta del servidor");
            setComments((prevComments) =>
              prevComments.filter((c) => c.id !== tempId)
            );
          }
        } catch (error) {
          // Si hay error de red, remover el comentario optimista
          console.error("Error al enviar comentario:", error);
          setComments((prevComments) =>
            prevComments.filter((c) => c.id !== tempId)
          );
        }
      },
      [commentText, isSubmitting, photo?.id, comments]
    );

    const handleLikeClick = useCallback(async () => {
      const uploaderName = localStorage.getItem("uploaderName");
      if (!uploaderName) {
        setShowPhotographerInvite(true);
        return;
      }

      // === OPTIMISTIC LIKE UPDATE ===
      const previousLikes = localLikes;
      setLocalLikes((prev) => prev + 1);
      setShowLikeAnimation(true);
      setTimeout(() => setShowLikeAnimation(false), 1000);

      try {
        // Obtener o generar un ID único de dispositivo
        let deviceId = localStorage.getItem("deviceId");
        if (!deviceId) {
          deviceId =
            "device_" +
            Date.now() +
            "_" +
            Math.random().toString(36).substr(2, 9);
          localStorage.setItem("deviceId", deviceId);
        }

        const response = await fetch(`/api/likes.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ photo_id: photo.id, user_id: deviceId }),
        });

        if (!response.ok) {
          throw new Error(`Error del servidor: ${response.statusText}`);
        }

        const data = await response.json();
        // Actualizar con el valor real del servidor
        setLocalLikes(data.total_likes);
        // Actualizar el contador de likes en el componente padre
        onLike(photo.id, data.total_likes);
      } catch (error) {
        // Revertir el like optimista
        setLocalLikes(previousLikes);
        console.error("Error al registrar el like:", error);
      }
    }, [photo?.id, localLikes, onLike]);

    const handleRegisterPromptClose = useCallback(() => {
      setShowRegisterPrompt(false);
    }, []);

    const handleRegisterPromptAccept = useCallback(() => {
      setShowRegisterPrompt(false);
      onRequestAuth();
    }, [onRequestAuth]);

    const handlePhotographerInviteClose = useCallback(() => {
      setShowPhotographerInvite(false);
    }, []);

    const handlePhotographerInviteAccept = useCallback(() => {
      setShowPhotographerInvite(false);
      onRequestAuth();
    }, [onRequestAuth]);

    if (!isOpen || !photo) return null;

    return (
      <>
        <AnimatePresence>
          <motion.div
            ref={modalContainerRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-white"
            style={{
              zIndex: 2147483647,
              height: "100vh",
              maxHeight: "100vh",
              overflow: "hidden",
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
            onClick={(e) => {
              // Solo cerrar si se hace click en el fondo, no en el contenido
              if (e.target === e.currentTarget) {
                onClose();
              }
            }}
          >
            {/* Botón cerrar - visible en móvil y desktop */}
            <button
              onClick={onClose}
              className="fixed top-4 right-4 z-[1000000] p-3 bg-black/50 backdrop-blur-sm rounded-full text-white hover:bg-black/70 transition-all shadow-lg"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Botones de navegación - visible en móvil y desktop */}
            {hasPrev && (
              <button
                onClick={onPrev}
                className="fixed top-1/2 -translate-y-1/2 left-4 z-[1000000] p-3 bg-black/50 backdrop-blur-sm rounded-full text-white hover:bg-black/70 transition-all shadow-lg"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
            )}

            {hasNext && (
              <button
                onClick={onNext}
                className="fixed top-1/2 -translate-y-1/2 right-4 lg:right-[420px] xl:right-[440px] z-[1000000] p-3 bg-black/50 backdrop-blur-sm rounded-full text-white hover:bg-black/70 transition-all shadow-lg"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            )}

            {/* Contenedor principal - altura fija sin overflow hacia galería */}
            <div
              className="w-full h-full mx-auto lg:flex lg:flex-row lg:max-w-7xl flex flex-col"
              style={{
                height: "100vh",
                maxHeight: "100vh",
                overflow: "hidden",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Contenido centrado sin scroll hacia galería */}
              <div
                className="flex-1 flex flex-col overflow-hidden"
                style={{
                  maxHeight: "100vh",
                }}
              >
                {/* MÓVIL/DESKTOP: Imagen estilo Polaroid */}
                <div className="relative w-full lg:h-full lg:flex-1 flex items-center justify-center px-3 py-12 sm:px-4 sm:py-16 lg:py-20 flex-shrink-0">
                  {/* Marco Polaroid Premium con diseño elegante */}
                  <div
                    className="relative bg-gradient-to-br from-white via-gray-50 to-gray-100 shadow-2xl w-full max-w-[min(95vw,28rem)]"
                    style={{
                      padding: "clamp(12px, 3vw, 20px)",
                      paddingBottom: "clamp(50px, 15vw, 80px)",
                      transform: "rotate(-1deg)",
                      boxShadow:
                        "0 20px 60px rgba(0,0,0,0.3), inset 0 0 20px rgba(0,0,0,0.05)",
                    }}
                  >
                    {/* Borde decorativo dorado */}
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        border: "2px solid transparent",
                        borderImage:
                          "linear-gradient(135deg, #d4af37, #f9e79f, #d4af37) 1",
                        opacity: 0.4,
                      }}
                    />

                    {/* Header con nombres elegantes */}
                    <div className="absolute -top-5 sm:-top-6 lg:-top-8 left-0 right-0 text-center">
                      <div className="inline-block bg-white px-4 py-1.5 sm:px-6 sm:py-2 lg:px-8 lg:py-3 rounded-full shadow-lg border-2 border-gray-200">
                        <h2
                          className="font-bold whitespace-nowrap"
                          style={{
                            fontSize: "clamp(18px, 5vw, 28px)",
                            fontFamily:
                              "'Dancing Script', 'Great Vibes', 'Allura', cursive",
                            fontWeight: "700",
                            color: "rgb(42, 81, 139)",
                            letterSpacing: "0.02em",
                            fontStyle: "italic",
                          }}
                        >
                          Dany & Miguel
                        </h2>
                      </div>
                    </div>

                    {/* Contenedor de la imagen con borde interno */}
                    <div
                      className="relative w-full bg-gray-100 border-4 border-white overflow-hidden"
                      style={{
                        aspectRatio: "1/1",
                        boxShadow: "inset 0 2px 8px rgba(0,0,0,0.1)",
                      }}
                      onClick={handleDoubleTap}
                    >
                      <img
                        src={photo?.url || ""}
                        alt={photo?.message || "Foto del evento"}
                        className="w-full h-full object-cover select-none"
                        draggable="false"
                        loading="eager"
                        onError={(e) => {
                          console.error("Error al cargar imagen:", photo?.url);
                        }}
                      />

                      {/* Viñeta sutil en las esquinas */}
                      <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          background:
                            "radial-gradient(ellipse at center, transparent 60%, rgba(0,0,0,0.15) 100%)",
                        }}
                      />

                      {/* Animación de like al hacer double-tap */}
                      {showLikeAnimation && (
                        <motion.div
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1.5, opacity: 1 }}
                          exit={{ scale: 2, opacity: 0 }}
                          transition={{ duration: 0.6 }}
                          className="absolute inset-0 flex items-center justify-center pointer-events-none z-10"
                        >
                          <Heart
                            className="w-24 h-24 text-white drop-shadow-2xl"
                            fill="currentColor"
                            strokeWidth={0}
                          />
                        </motion.div>
                      )}
                    </div>

                    {/* Sección inferior con fecha y mensaje - estilo premium */}
                    <div className="absolute bottom-3 sm:bottom-4 left-4 sm:left-6 right-4 sm:right-6">
                      {/* Fecha elegante con iconos decorativos */}
                      <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                        <div className="w-4 sm:w-8 h-px bg-gradient-to-r from-transparent via-rose-400 to-transparent" />
                        <div className="bg-gradient-to-br from-rose-100 to-pink-100 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg border border-rose-200 shadow-sm">
                          <p className="text-rose-700 text-[10px] sm:text-xs font-semibold tracking-wider whitespace-nowrap">
                            21 • Febrero • 2026
                          </p>
                        </div>
                        <div className="w-4 sm:w-8 h-px bg-gradient-to-r from-transparent via-rose-400 to-transparent" />
                      </div>

                      {/* Mensaje o subtítulo */}
                      {photo?.message ? (
                        <p className="text-gray-700 text-xs sm:text-sm font-serif text-center line-clamp-2 italic px-1">
                          "{photo?.message}"
                        </p>
                      ) : (
                        <p className="text-gray-500 text-[10px] sm:text-xs text-center font-serif italic">
                          Un momento inolvidable
                        </p>
                      )}
                    </div>

                    {/* Decoración: corazones pequeños en esquinas */}
                    <div className="absolute top-2 sm:top-3 left-2 sm:left-3 text-rose-300 opacity-40">
                      <svg
                        width="12"
                        height="12"
                        className="sm:w-4 sm:h-4"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                      </svg>
                    </div>
                    <div className="absolute top-2 sm:top-3 right-2 sm:right-3 text-rose-300 opacity-40">
                      <svg
                        width="12"
                        height="12"
                        className="sm:w-4 sm:h-4"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* MÓVIL: Acciones e info en la parte inferior - altura limitada */}
                <div
                  className="lg:hidden w-full bg-white flex flex-col flex-shrink-0 overflow-hidden"
                  style={{ maxHeight: "35vh" }}
                >
                  {/* Header con usuario */}
                  <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold">
                        {photo?.uploader?.charAt(0)?.toUpperCase() || "?"}
                      </div>
                      <div>
                        <p className="font-semibold text-sm">
                          {photo?.uploader || "Anónimo"}
                        </p>
                        <p className="text-gray-500 text-xs">
                          {photo?.created_at
                            ? formatDateMX(photo.created_at)
                            : ""}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Acciones (Like y Comentarios) */}
                  <div className="p-4 border-b border-gray-200 flex items-center gap-6 flex-shrink-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLikeClick();
                      }}
                      className="flex items-center gap-2 hover:opacity-70 transition-opacity active:scale-95"
                    >
                      <Heart
                        className="w-7 h-7 text-red-500"
                        fill={localLikes > 0 ? "currentColor" : "none"}
                      />
                      <span className="text-base font-semibold">
                        {localLikes}
                      </span>
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCommentClick();
                      }}
                      className="flex items-center gap-2 hover:opacity-70 transition-opacity active:scale-95"
                    >
                      <MessageSquare className="w-7 h-7 text-gray-700" />
                      <span className="text-base font-semibold">
                        {comments?.length || 0}
                      </span>
                    </button>
                  </div>

                  {/* Preview de comentarios - sin scroll hacia galería */}
                  <div className="w-full p-4 space-y-3 overflow-hidden flex-1">
                    {!comments || comments.length === 0 ? (
                      <div className="text-center py-4 text-gray-400">
                        <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No hay comentarios</p>
                        <button
                          onClick={handleCommentClick}
                          className="mt-2 text-sm text-primary font-semibold"
                        >
                          Sé el primero en comentar
                        </button>
                      </div>
                    ) : (
                      <>
                        {comments.slice(0, 3).map((comment, index) => (
                          <div key={index} className="flex gap-2">
                            <div className="w-7 h-7 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                              {comment.author?.charAt(0)?.toUpperCase()}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm">
                                <span className="font-semibold mr-1">
                                  {comment.author}
                                </span>
                                <span className="text-gray-800">
                                  {comment.text}
                                </span>
                              </p>
                            </div>
                          </div>
                        ))}
                        {comments?.length > 3 && (
                          <button
                            onClick={handleCommentClick}
                            className="text-sm text-gray-500 w-full text-center py-2"
                          >
                            Ver todos los {comments?.length || 0} comentarios
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                {/* Fin del área scrollable */}
              </div>

              {/* DESKTOP: Panel lateral de comentarios */}
              <div className="hidden lg:flex lg:w-96 xl:w-[420px] flex-col bg-white border-l border-gray-200 h-full">
                {/* Header con usuario */}
                <div className="p-4 border-b border-gray-200 flex items-center gap-3 flex-shrink-0">
                  <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold">
                    {photo?.uploader?.charAt(0)?.toUpperCase() || "?"}
                  </div>
                  <div>
                    <p className="font-semibold text-sm">
                      {photo?.uploader || "Anónimo"}
                    </p>
                    <p className="text-gray-500 text-xs">
                      {photo?.created_at ? formatDateMX(photo.created_at) : ""}
                    </p>
                  </div>
                </div>

                {/* Acciones (Like y Comentarios) */}
                <div className="p-4 border-b border-gray-200 flex items-center gap-6 flex-shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLikeClick();
                    }}
                    className="flex items-center gap-2 hover:opacity-70 transition-opacity active:scale-95"
                  >
                    <Heart
                      className="w-6 h-6 text-red-500"
                      fill={localLikes > 0 ? "currentColor" : "none"}
                    />
                    <span className="text-base font-semibold">
                      {localLikes}
                    </span>
                  </button>

                  <div className="flex items-center gap-2 text-gray-700">
                    <MessageSquare className="w-6 h-6" />
                    <span className="text-base font-semibold">
                      {comments?.length || 0}
                    </span>
                  </div>
                </div>

                {/* Lista de comentarios con scroll */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {!comments || comments.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No hay comentarios aún</p>
                      <p className="text-xs mt-1">
                        ¡Sé el primero en comentar!
                      </p>
                    </div>
                  ) : (
                    comments.map((comment, index) => (
                      <div key={comment.id || index} className="flex gap-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {comment.author?.charAt(0)?.toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm break-words">
                            <span className="font-semibold mr-2">
                              {comment.author}
                            </span>
                            <span className="text-gray-800">
                              {comment.text}
                            </span>
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {formatDateTimeMX(comment.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Input de comentario fijo abajo */}
                <div className="p-4 border-t border-gray-200 flex-shrink-0 bg-white">
                  <form
                    onSubmit={handleCommentSubmit}
                    className="flex items-center gap-3"
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {localStorage
                        .getItem("uploaderName")
                        ?.charAt(0)
                        ?.toUpperCase() || "?"}
                    </div>
                    <input
                      type="text"
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder="Escribe un comentario..."
                      className="flex-1 px-4 py-2.5 bg-gray-100 border-0 rounded-full focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                      maxLength={500}
                      disabled={isSubmitting}
                    />
                    <button
                      type="submit"
                      disabled={!commentText.trim() || isSubmitting}
                      className="p-2.5 bg-gradient-to-r from-primary to-secondary text-white rounded-full hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                    >
                      {isSubmitting ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>

            {/* MÓVIL: Panel de comentarios a pantalla completa */}
            <AnimatePresence mode="wait">
              {showComments && (
                <>
                  {/* Panel de comentarios - diseño estilo Instagram optimizado para iOS */}
                  <motion.div
                    ref={commentsPanelRef}
                    key="comments-panel"
                    initial={{ opacity: 0, y: "100%" }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: "100%" }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    className="lg:hidden"
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      height: "100%",
                      width: "100%",
                      backgroundColor: "white",
                      zIndex: 2147483648,
                      display: "flex",
                      flexDirection: "column",
                      // Clave para iOS: usar overflow visible y dejar que Safari maneje el scroll
                      WebkitOverflowScrolling: "touch",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Header del panel con botón de cerrar */}
                    <div
                      className="flex items-center justify-between bg-white"
                      style={{
                        padding: "16px",
                        borderBottom: "1px solid #e5e7eb",
                        flexShrink: 0,
                        minHeight: "60px",
                      }}
                    >
                      <h3
                        style={{ fontWeight: 600, fontSize: "20px", margin: 0 }}
                      >
                        Comentarios
                      </h3>
                      <button
                        onClick={() => setShowComments(false)}
                        className="p-2 hover:bg-gray-100 rounded-full transition-all"
                      >
                        <X className="w-6 h-6 text-gray-700" />
                      </button>
                    </div>

                    {/* Foto miniatura para referencia */}
                    <div
                      className="flex items-center gap-3"
                      style={{
                        padding: "12px 16px",
                        backgroundColor: "#f9fafb",
                        borderBottom: "1px solid #e5e7eb",
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={photo?.url || ""}
                        alt="Foto"
                        className="w-14 h-14 object-cover rounded-lg shadow-sm"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-700 truncate">
                          {photo?.uploader || "Anónimo"}
                        </p>
                        <p className="text-xs text-gray-500">
                          {comments?.length || 0} comentario
                          {(comments?.length || 0) !== 1 ? "s" : ""}
                        </p>
                      </div>
                    </div>

                    {/* Lista de comentarios con scroll */}
                    <div
                      ref={commentsListRef}
                      style={{
                        flex: "1 1 0%",
                        overflowY: "auto",
                        overflowX: "hidden",
                        padding: "16px",
                        backgroundColor: "white",
                        minHeight: 0,
                        WebkitOverflowScrolling: "touch",
                        overscrollBehavior: "contain",
                      }}
                    >
                      {!comments || comments.length === 0 ? (
                        <div className="text-center py-12 text-gray-400">
                          <MessageSquare className="w-16 h-16 mx-auto mb-3 opacity-30" />
                          <p className="text-base font-medium">
                            No hay comentarios aún
                          </p>
                          <p className="text-sm mt-1">
                            ¡Sé el primero en comentar!
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {comments.map((comment, index) => (
                            <div
                              key={comment.id || index}
                              className="flex gap-3 animate-fadeIn"
                            >
                              <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 shadow-sm">
                                {comment.author?.charAt(0)?.toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0 bg-gray-50 rounded-2xl p-3">
                                <p className="text-sm break-words">
                                  <span className="font-semibold text-gray-900">
                                    {comment.author}
                                  </span>
                                </p>
                                <p className="text-sm text-gray-700 mt-1">
                                  {comment.text}
                                </p>
                                <p className="text-xs text-gray-400 mt-2">
                                  {formatDateTimeMX(comment.timestamp)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Input de comentario - con manejo especial para iOS keyboard */}
                    <div
                      style={{
                        flexShrink: 0,
                        backgroundColor: "white",
                        borderTop: "1px solid #e5e7eb",
                        padding: "12px 16px",
                        paddingBottom: "max(12px, env(safe-area-inset-bottom))",
                        // Clave: position sticky para que iOS lo maneje bien con el teclado
                        position: "sticky",
                        bottom: 0,
                        left: 0,
                        right: 0,
                      }}
                    >
                      <form
                        onSubmit={handleCommentSubmit}
                        className="flex items-center gap-3"
                      >
                        <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {localStorage
                            .getItem("uploaderName")
                            ?.charAt(0)
                            ?.toUpperCase() || "?"}
                        </div>
                        <input
                          ref={commentsInputRef}
                          type="text"
                          value={commentText}
                          onChange={(e) => setCommentText(e.target.value)}
                          onFocus={() => setIsInputFocused(true)}
                          onBlur={() => {
                            setIsInputFocused(false);
                            // Forzar scroll al top después de cerrar el teclado
                            setTimeout(() => window.scrollTo(0, 0), 100);
                          }}
                          placeholder="Escribe un comentario..."
                          className="flex-1 px-4 py-3 bg-gray-100 border-0 rounded-full focus:outline-none focus:ring-2 focus:ring-primary/50"
                          style={{
                            fontSize: "16px", // Previene zoom en iOS
                            lineHeight: "1.5",
                          }}
                          maxLength={500}
                          disabled={isSubmitting}
                          autoComplete="off"
                          autoCorrect="off"
                        />
                        <button
                          type="submit"
                          disabled={!commentText.trim() || isSubmitting}
                          className="p-3 bg-gradient-to-r from-primary to-secondary text-white rounded-full hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0 shadow-lg"
                        >
                          {isSubmitting ? (
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Send className="w-5 h-5" />
                          )}
                        </button>
                      </form>
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </motion.div>
        </AnimatePresence>

        {/* RegisterPrompt Modal */}
        <RegisterPrompt
          isOpen={showRegisterPrompt}
          onClose={handleRegisterPromptClose}
          onRegister={handleRegisterPromptAccept}
        />

        {/* PhotographerInvite Modal */}
        <PhotographerInvite
          isOpen={showPhotographerInvite}
          onClose={handlePhotographerInviteClose}
          onRegister={handlePhotographerInviteAccept}
        />
      </>
    );
  }
);

// Display name para debugging
PhotoModal.displayName = "PhotoModal";

export default PhotoModal;
